<template>
  <div class="sales-by-customer">
    <!-- Header -->
    <div class="bg-dark m-b-30">
      <div class="container">
        <div class="row p-b-30 p-t-30">
          <div class="col-md-12 text-center">
            <h2 class="text-white">
              <i class="mdi mdi-account-group"></i> Customer Purchase Insights
            </h2>
            <p class="text-white opacity-75">Comprehensive customer behaviour analysis and revenue attribution</p>
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <!-- Filters -->
      <el-card class="m-b-20">
        <div slot="header" class="card-header">
          <h4><i class="mdi mdi-filter-variant"></i> Analysis Filters</h4>
          <el-button type="text" @click="resetFilters">
            <i class="el-icon-refresh"></i> Reset
          </el-button>
        </div>
        <el-form :inline="true" size="small" label-width="110px">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Date Range">
                <el-date-picker
                  v-model="filters.dateRange"
                  type="daterange"
                  range-separator="to"
                  start-placeholder="Start date"
                  end-placeholder="End date"
                  format="MMM dd, yyyy"
                  value-format="yyyy-MM-dd"
                  :shortcuts="dateShortcuts"
                  @change="handleDateChange"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Sort By">
                <el-select v-model="filters.sort_by" placeholder="Sort by" @change="loadData">
                  <el-option label="Revenue (High to Low)" value="total_revenue_desc" />
                  <el-option label="Revenue (Low to High)" value="total_revenue_asc" />
                  <el-option label="Orders (High to Low)" value="total_orders_desc" />
                  <el-option label="Orders (Low to High)" value="total_orders_asc" />
                  <el-option label="Avg Order Value (High to Low)" value="average_order_value_desc" />
                  <el-option label="Customer Name" value="customer_name" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="Items Per Page">
                <el-select v-model="filters.per_page" placeholder="Per page" @change="loadData">
                  <el-option label="10" :value="10" />
                  <el-option label="25" :value="25" />
                  <el-option label="50" :value="50" />
                  <el-option label="100" :value="100" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="loadData" :loading="loading">
                  Search
                </el-button>
                <el-button type="success" icon="el-icon-download" @click="exportData" :loading="exporting">
                  Export
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </el-card>

      <!-- KPI Cards -->
      <el-row :gutter="20" class="m-b-20">
        <el-col :xs="24" :sm="12" :md="6" v-for="stat in summaryStats" :key="stat.title" class="m-b-20">
          <el-card :body-style="{ padding: '20px' }" class="stat-card">
            <div class="stat-content">
              <div class="stat-info">
                <h4 class="stat-title">{{ stat.title }}</h4>
                <h2 class="stat-value">{{ stat.value }}</h2>
                <div class="stat-comparison" v-if="stat.trend !== null">
                  <span :class="['trend', stat.trend >= 0 ? 'positive' : 'negative']">
                    <i :class="stat.trend >= 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    {{ Math.abs(stat.trend) }}%
                  </span>
                  <span class="comparison-text">vs previous period</span>
                </div>
              </div>
              <div class="stat-icon">
                <i :class="stat.icon"></i>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- Loading -->
      <div v-if="loading" class="text-center p-40">
        <i class="el-icon-loading" style="font-size: 24px;"></i>
        <p>Loading customer analysis...</p>
      </div>

      <div v-else>
        <!-- Charts Row -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Bar chart: top customers by revenue -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-bar"></i> Top Customers by Revenue</h4>
                <el-select v-model="chartMetric" size="mini" @change="updateChartData">
                  <el-option label="Revenue" value="total_revenue" />
                  <el-option label="Orders" value="total_orders" />
                  <el-option label="Avg Order Value" value="average_order_value" />
                </el-select>
              </div>
              <div class="chart-container">
                <bar-chart
                  v-if="customerData.length > 0"
                  :chart-data="customerChartData"
                  :chart-options="customerChartOptions"
                  :height="300"
                />
                <div v-else class="empty-state">
                  <i class="mdi mdi-chart-bar"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Doughnut: revenue share -->
          <el-col :xs="24" :lg="12" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-chart-pie"></i> Revenue Share by Customer</h4>
              </div>
              <div class="chart-container">
                <doughnut-chart
                  v-if="customerData.length > 0"
                  :chart-data="revenueShareChartData"
                  :chart-options="revenueShareChartOptions"
                  :height="300"
                />
                <div v-else class="empty-state">
                  <i class="mdi mdi-chart-pie"></i>
                  <p>No data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Top customers list + Table -->
        <el-row :gutter="20" class="m-b-20">
          <!-- Top 5 leaderboard -->
          <el-col :xs="24" :lg="8" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-trophy"></i> Top Customers</h4>
              </div>
              <div class="top-list">
                <div
                  v-for="(customer, index) in topCustomers"
                  :key="customer.customer_id"
                  class="top-item"
                >
                  <div class="top-rank" :class="'rank-' + (index + 1)">{{ index + 1 }}</div>
                  <div class="top-info">
                    <div class="top-name">{{ customer.customer_name }}</div>
                    <div class="top-stats">
                      <span class="revenue">{{ formatCurrency(customer.total_revenue) }}</span>
                      <span class="orders">{{ customer.total_orders }} orders</span>
                    </div>
                    <el-progress
                      :percentage="topCustomers.length > 0 && Number(topCustomers[0].total_revenue) > 0 ? Math.min(100, Math.max(0, Math.round((Number(customer.total_revenue) / Number(topCustomers[0].total_revenue)) * 100))) : 0"
                      :stroke-width="4"
                      :show-text="false"
                      :color="getProgressColor(index)"
                    />
                  </div>
                  <div class="top-growth">
                    <span :class="['growth-badge', customer.growth_rate >= 0 ? 'positive' : 'negative']">
                      <i :class="customer.growth_rate >= 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                      {{ Math.abs(customer.growth_rate || 0) }}%
                    </span>
                  </div>
                </div>
                <div v-if="topCustomers.length === 0" class="empty-state-small">
                  <p>No customer data</p>
                </div>
              </div>
            </el-card>
          </el-col>

          <!-- Lifetime value mini panel (from CLV endpoint) -->
          <el-col :xs="24" :lg="16" class="m-b-20">
            <el-card>
              <div slot="header" class="card-header">
                <h4><i class="mdi mdi-account-star"></i> Customer Lifetime Value</h4>
                <el-tag size="mini" type="info">Top 10 by CLV</el-tag>
              </div>
              <div class="chart-container">
                <bar-chart
                  v-if="clvData.length > 0"
                  :chart-data="clvChartData"
                  :chart-options="clvChartOptions"
                  :height="250"
                />
                <div v-else class="empty-state">
                  <i class="mdi mdi-account-star"></i>
                  <p>No CLV data available</p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- Full Data Table -->
        <el-card>
          <div slot="header" class="card-header">
            <h4><i class="mdi mdi-table"></i> Customer Analysis</h4>
            <el-tag type="info">{{ pagination.total }} customers found</el-tag>
          </div>
          <el-table
            :data="customerData"
            style="width: 100%"
            size="small"
            :default-sort="{ prop: 'total_revenue', order: 'descending' }"
            @sort-change="handleSortChange"
          >
            <el-table-column type="index" label="#" width="50" />
            <el-table-column prop="customer_name" label="Customer" sortable="custom" min-width="160">
              <template slot-scope="scope">
                <div class="customer-cell">
                  <div class="customer-avatar">{{ (scope.row.customer_name || '?')[0].toUpperCase() }}</div>
                  <span>{{ scope.row.customer_name || 'Unknown' }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="total_orders" label="Orders" sortable="custom" width="90" align="right" />
            <el-table-column prop="total_revenue" label="Revenue" sortable="custom" width="150" align="right">
              <template slot-scope="scope">
                <strong>{{ formatCurrency(scope.row.total_revenue) }}</strong>
              </template>
            </el-table-column>
            <el-table-column prop="average_order_value" label="Avg Order" sortable="custom" width="140" align="right">
              <template slot-scope="scope">
                {{ formatCurrency(scope.row.average_order_value) }}
              </template>
            </el-table-column>
            <el-table-column prop="market_share" label="Market Share" width="120" align="right">
              <template slot-scope="scope">
                <el-tooltip :content="formatCurrency(scope.row.total_revenue)" placement="top">
                  <span>{{ (scope.row.market_share || 0).toFixed(1) }}%</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column prop="growth_rate" label="Growth" sortable="custom" width="100" align="center">
              <template slot-scope="scope">
                <span :class="['growth-tag', (scope.row.growth_rate || 0) >= 0 ? 'positive' : 'negative']">
                  <i :class="(scope.row.growth_rate || 0) >= 0 ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                  {{ Math.abs(scope.row.growth_rate || 0) }}%
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Revenue Bar" min-width="140">
              <template slot-scope="scope">
                <el-progress
                  :percentage="Math.min(100, Math.max(0, Number(scope.row.market_share) || 0))"
                  :stroke-width="6"
                  :show-text="false"
                  :color="getProgressColor(scope.$index)"
                />
              </template>
            </el-table-column>
          </el-table>

          <!-- Pagination -->
          <div class="pagination-wrapper">
            <el-pagination
              background
              layout="total, sizes, prev, pager, next"
              :total="pagination.total"
              :page-size="filters.per_page"
              :page-sizes="[10, 25, 50, 100]"
              :current-page="pagination.current_page"
              @size-change="handlePageSizeChange"
              @current-change="handlePageChange"
            />
          </div>
        </el-card>
      </div>
    </div>

    <!-- Detail Dialog -->
    <el-dialog :title="selectedCustomer ? selectedCustomer.customer_name : ''" :visible.sync="dialogVisible" width="70%">
      <div v-if="selectedCustomer">
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="detail-stat">
              <div class="detail-label">Total Revenue</div>
              <div class="detail-value primary">{{ formatCurrency(selectedCustomer.total_revenue) }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="detail-stat">
              <div class="detail-label">Total Orders</div>
              <div class="detail-value">{{ selectedCustomer.total_orders }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="detail-stat">
              <div class="detail-label">Avg Order Value</div>
              <div class="detail-value">{{ formatCurrency(selectedCustomer.average_order_value) }}</div>
            </div>
          </el-col>
        </el-row>
      </div>
      <span slot="footer">
        <el-button @click="dialogVisible = false">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import BarChart from '@/components/_Partials/Charts/BarChart.vue'
import DoughnutChart from '@/components/_Partials/Charts/DoughnutChart.vue'

const CHART_COLORS = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#9C27B0', '#FF9800', '#795548', '#00BCD4', '#607D8B']

export default {
  name: 'SalesByCustomer',
  components: { BarChart, DoughnutChart },

  data () {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 30)
    const fmt = d => d.toISOString().split('T')[0]

    return {
      loading: false,
      exporting: false,
      dialogVisible: false,
      selectedCustomer: null,
      chartMetric: 'total_revenue',

      filters: {
        dateRange: [fmt(start), fmt(end)],
        start_date: fmt(start),
        end_date: fmt(end),
        sort_by: 'total_revenue_desc',
        per_page: 25,
        page: 1
      },

      customerData: [],
      clvData: [],
      pagination: { total: 0, current_page: 1, last_page: 1 },

      // Computed chart data
      customerChartData: { labels: [], datasets: [] },
      revenueShareChartData: { labels: [], datasets: [] },
      clvChartData: { labels: [], datasets: [] },

      dateShortcuts: [
        { text: 'Today', value: () => { const d = new Date(); return [d, d] } },
        { text: 'Last 7 days', value: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 6); return [s, e] } },
        { text: 'Last 30 days', value: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 29); return [s, e] } },
        { text: 'This month', value: () => { const e = new Date(); return [new Date(e.getFullYear(), e.getMonth(), 1), e] } },
        { text: 'Last month', value: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth() - 1, 1); const e = new Date(n.getFullYear(), n.getMonth(), 0); return [s, e] } }
      ],

      customerChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true } }
      },

      revenueShareChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } }
      },

      clvChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true } }
      }
    }
  },

  computed: {
    topCustomers () {
      return this.customerData.slice(0, 5)
    },

    summaryStats () {
      if (!this.customerData.length) {
        return [
          { title: 'Total Customers', value: '—', trend: null, icon: 'mdi mdi-account-group' },
          { title: 'Total Revenue', value: '—', trend: null, icon: 'mdi mdi-currency-fra' },
          { title: 'Avg Order Value', value: '—', trend: null, icon: 'mdi mdi-chart-line' },
          { title: 'Top Customer', value: '—', trend: null, icon: 'mdi mdi-trophy' }
        ]
      }
      const totalRevenue = this.customerData.reduce((s, c) => s + (c.total_revenue || 0), 0)
      const totalOrders = this.customerData.reduce((s, c) => s + (c.total_orders || 0), 0)
      const avgOrder = totalOrders > 0 ? totalRevenue / totalOrders : 0
      const top = this.topCustomers[0]
      return [
        {
          title: 'Total Customers',
          value: this.pagination.total,
          trend: null,
          icon: 'mdi mdi-account-group'
        },
        {
          title: 'Total Revenue',
          value: this.formatCurrency(totalRevenue),
          trend: null,
          icon: 'mdi mdi-currency-fra'
        },
        {
          title: 'Avg Order Value',
          value: this.formatCurrency(avgOrder),
          trend: null,
          icon: 'mdi mdi-chart-line'
        },
        {
          title: 'Top Customer',
          value: top ? top.customer_name : '—',
          trend: top ? (top.growth_rate || 0) : null,
          icon: 'mdi mdi-trophy'
        }
      ]
    }
  },

  mounted () {
    this.loadData()
    this.loadClvData()
  },

  methods: {
    handleDateChange (dates) {
      if (dates && dates.length === 2) {
        this.filters.start_date = dates[0]
        this.filters.end_date = dates[1]
        this.filters.page = 1
        this.loadData()
      }
    },

    resetFilters () {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 30)
      const fmt = d => d.toISOString().split('T')[0]
      this.filters = {
        dateRange: [fmt(start), fmt(end)],
        start_date: fmt(start),
        end_date: fmt(end),
        sort_by: 'total_revenue_desc',
        per_page: 25,
        page: 1
      }
      this.loadData()
    },

    async loadData () {
      this.loading = true
      try {
        const params = {
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          sort_by: this.filters.sort_by,
          per_page: this.filters.per_page,
          page: this.filters.page
        }
        const response = await this.$reportService.getSalesByCustomer(params)
        if (response.status === 'success') {
          this.customerData = response.data.data || response.data || []
          const meta = response.data.meta || response.meta || {}
          this.pagination = {
            total: meta.total || this.customerData.length,
            current_page: meta.current_page || 1,
            last_page: meta.last_page || 1
          }
          this.updateChartData()
        }
      } catch (error) {
        console.error('Error loading customer data:', error)
        this.$message && this.$message.error('Failed to load customer data')
      } finally {
        this.loading = false
      }
    },

    async loadClvData () {
      try {
        const params = {
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          per_page: 10
        }
        const response = await this.$reportService.getCustomerLifetimeValue(params)
        if (response.status === 'success') {
          this.clvData = (response.data.data || response.data || []).slice(0, 10)
          this.buildClvChart()
        }
      } catch (error) {
        console.error('Error loading CLV data:', error)
      }
    },

    updateChartData () {
      const top10 = this.customerData.slice(0, 10)
      const labels = top10.map(c => c.customer_name || 'Unknown')
      const metric = this.chartMetric

      this.customerChartData = {
        labels,
        datasets: [{
          label: this.metricLabel(metric),
          data: top10.map(c => parseFloat((c[metric] || 0).toFixed(2))),
          backgroundColor: top10.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderWidth: 1
        }]
      }

      const totalRevenue = top10.reduce((s, c) => s + (c.total_revenue || 0), 0)
      this.revenueShareChartData = {
        labels,
        datasets: [{
          data: top10.map(c => parseFloat((c.total_revenue || 0).toFixed(2))),
          backgroundColor: top10.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderWidth: 2,
          borderColor: '#fff'
        }]
      }
    },

    buildClvChart () {
      const labels = this.clvData.map(c => c.customer_name || 'Unknown')
      this.clvChartData = {
        labels,
        datasets: [{
          label: 'Lifetime Value',
          data: this.clvData.map(c => parseFloat((c.lifetime_value || c.total_revenue || 0).toFixed(2))),
          backgroundColor: labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
          borderWidth: 1
        }]
      }
    },

    metricLabel (metric) {
      const map = { total_revenue: 'Revenue', total_orders: 'Orders', average_order_value: 'Avg Order Value' }
      return map[metric] || metric
    },

    handleSortChange ({ prop, order }) {
      if (!prop) return
      const dir = order === 'ascending' ? 'asc' : 'desc'
      this.filters.sort_by = `${prop}_${dir}`
      this.filters.page = 1
      this.loadData()
    },

    handlePageChange (page) {
      this.filters.page = page
      this.loadData()
    },

    handlePageSizeChange (size) {
      this.filters.per_page = size
      this.filters.page = 1
      this.loadData()
    },

    async exportData () {
      this.exporting = true
      try {
        const { dateRange, start_date: startDate, end_date: endDate, ...rest } = this.filters
        const response = await this.$reportService.getSalesByCustomer({
          ...rest,
          start_date: startDate || (dateRange && dateRange[0]),
          end_date: endDate || (dateRange && dateRange[1]),
          dl: 'csv'
        })
        if (response.url) {
          window.open(response.url, '_blank')
        } else {
          this.$message && this.$message.info('Export feature coming soon')
        }
      } catch (error) {
        this.$message && this.$message.error('Export failed')
      } finally {
        this.exporting = false
      }
    },

    formatCurrency (value) {
      return new Intl.NumberFormat('fr-CM', { style: 'currency', currency: 'XAF', maximumFractionDigits: 0 }).format(value || 0)
    },

    getProgressColor (index) {
      return CHART_COLORS[index % CHART_COLORS.length]
    }
  }
}
</script>

<style scoped>
.sales-by-customer {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.bg-dark {
  background: linear-gradient(135deg, #1a237e 0%, #283593 100%);
}

.m-b-20 { margin-bottom: 20px; }
.m-b-30 { margin-bottom: 30px; }
.p-b-30 { padding-bottom: 30px; }
.p-t-30 { padding-top: 30px; }
.p-40 { padding: 40px; }
.text-center { text-align: center; }
.text-muted { color: #909399; }
.opacity-75 { opacity: 0.75; }

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h4 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
}

.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stat-info { flex: 1; }

.stat-title {
  color: #606266;
  font-size: 13px;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.stat-value {
  color: #303133;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.stat-comparison {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend {
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 2px;
}

.trend.positive { color: #67C23A; }
.trend.negative { color: #F56C6C; }
.comparison-text { font-size: 12px; color: #909399; }

.stat-icon {
  font-size: 36px;
  color: #409EFF;
  opacity: 0.25;
  margin-left: 10px;
}

.chart-container {
  position: relative;
  min-height: 200px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #C0C4CC;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 10px;
}

.empty-state-small {
  text-align: center;
  padding: 20px;
  color: #909399;
}

.top-list {
  max-height: 360px;
  overflow-y: auto;
}

.top-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #EBEEF5;
  gap: 10px;
}

.top-item:last-child { border-bottom: none; }

.top-rank {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
  background: #909399;
}

.top-rank.rank-1 { background: #FFD700; color: #333; }
.top-rank.rank-2 { background: #C0C0C0; color: #333; }
.top-rank.rank-3 { background: #CD7F32; }

.top-info { flex: 1; min-width: 0; }

.top-name {
  font-weight: 600;
  color: #303133;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 3px;
}

.top-stats {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #606266;
  margin-bottom: 5px;
}

.top-growth {
  flex-shrink: 0;
}

.growth-badge {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.growth-badge.positive { color: #67C23A; background: #f0f9eb; }
.growth-badge.negative { color: #F56C6C; background: #fef0f0; }

.growth-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
}

.growth-tag.positive { color: #67C23A; }
.growth-tag.negative { color: #F56C6C; }

.customer-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.customer-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF, #1a237e);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
}

.detail-stat {
  text-align: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.detail-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 20px;
  font-weight: 700;
  color: #303133;
}

.detail-value.primary { color: #409EFF; }
</style>
